import { range, truncate } from "lodash";
import { Fragment, PropsWithChildren } from "react";

import Tooltip from "@components/tooltip/tooltip";
import { classNames } from "@helpers/css";

import { NineBoxQuestion } from "./types";

const Grid = ({
  children,
  rows,
  cols,
}: PropsWithChildren<{ rows: number; cols: number }>) => (
  <div
    className={classNames("flex-1", "grid", "gap-4", `grid-cols-${cols}`)}
    style={{
      gridTemplateRows: `repeat(${rows}, 10rem) 3rem`,
    }}
  >
    {children}
  </div>
);

const NineBoxGrid = ({
  xQuestion,
  yQuestion,
  cellRenderer,
}: {
  xQuestion: NineBoxQuestion;
  yQuestion: NineBoxQuestion;
  cellRenderer: (opts: { xValue: number; yValue: number }) => JSX.Element;
}) => {
  const cols = xQuestion.endValue - xQuestion.startValue + 2;
  return (
    <div className="m-auto max-w-[900px]">
      <div className="flex items-center gap-2">
        <Tooltip text={yQuestion.title}>
          <div
            className="w-12 max-h-96 shrink-0 font-medium text-center"
            style={{
              writingMode: "sideways-lr",
              position: "relative",
              left: "25px",
            }}
          >
            {truncate(yQuestion.title, { length: 90 })}
          </div>
        </Tooltip>
        <Grid rows={yQuestion.endValue - yQuestion.startValue + 1} cols={cols}>
          {range(yQuestion.startValue, yQuestion.endValue + 1)
            .reverse()
            .map((yValue, index) => (
              <Fragment key={yValue}>
                <div className="flex items-center justify-center ml-8 text-center">
                  <Tooltip
                    text={yQuestion.labels.toReversed()[index] || `${yValue}`}
                  >
                    <div>
                      {truncate(
                        yQuestion.labels.toReversed()[index] || `${yValue}`,
                        {
                          length: 50,
                        }
                      )}
                    </div>
                  </Tooltip>
                </div>
                {range(xQuestion.startValue, xQuestion.endValue + 1).map(
                  (xValue) => {
                    return cellRenderer({
                      xValue,
                      yValue,
                    });
                  }
                )}
              </Fragment>
            ))}
          <div className="h-12"></div>
          {range(xQuestion.startValue, xQuestion.endValue + 1).map(
            (value, index) => (
              <Tooltip key={value} text={xQuestion.labels[index] || `${value}`}>
                <div className="flex items-center justify-center text-center h-12">
                  {truncate(xQuestion.labels[index] || `${value}`, {
                    length: 50,
                  })}
                </div>
              </Tooltip>
            )
          )}
        </Grid>
      </div>
      <div className="flex items-center gap-2">
        <div className="w-12 shrink-0"></div>
        <div
          className="flex-1 grid gap-4"
          style={{ gridTemplateColumns: `1fr ${cols - 1}fr` }}
        >
          <div></div>
          <Tooltip text={xQuestion.title}>
            <div className="font-medium text-center">
              {truncate(xQuestion.title, { length: 90 })}
            </div>
          </Tooltip>
        </div>
      </div>
    </div>
  );
};

export const SingleQuestionNineBoxGrid = ({
  question,
  cellRenderer,
}: {
  question: NineBoxQuestion;
  cellRenderer: (opts: { value: number }) => JSX.Element;
}) => {
  const cols = question.endValue - question.startValue + 1;
  return (
    <div className="m-auto max-w-[900px]">
      <Grid rows={1} cols={cols}>
        {range(question.startValue, question.endValue + 1).map((value) => {
          return cellRenderer({
            value,
          });
        })}
        {range(question.startValue, question.endValue + 1).map(
          (value, index) => (
            <Tooltip key={value} text={question.labels[index] || `${value}`}>
              <div className="flex items-center justify-center text-center h-12">
                {truncate(question.labels[index] || `${value}`, {
                  length: 50,
                })}
              </div>
            </Tooltip>
          )
        )}
      </Grid>
      <div className="grid gap-4" style={{ gridTemplateColumns: `${cols}fr` }}>
        <Tooltip text={question.title}>
          <div className="font-medium text-center">
            {truncate(question.title, { length: 90 })}
          </div>
        </Tooltip>
      </div>
    </div>
  );
};

export default NineBoxGrid;
